import { Capacitor } from '@capacitor/core'
import { ArrowLeft, Download } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { closePdfPreview, usePdfPreview } from '@/hooks/usePdfPreview'
import { saveOrShareBlob } from '@/utils/nativeFileShare'

/**
 * Full-screen in-app PDF preview shown after a native PDF export/print finishes
 * rasterizing (see `printToPdfAndShare` in `src/utils/exportUtils.ts`). Replaces
 * immediately handing the generated PDF to the OS share sheet with a stack of the
 * already-rasterized page images the user can scroll through, with an explicit
 * Download action once they're ready to save/share it. Renders nothing unless a
 * preview has been opened via `openPdfPreview` — the `Capacitor.isNativePlatform()`
 * guard is defense-in-depth, since nothing calls `openPdfPreview` on web today
 * (mirrors `AppLockGate`'s equivalent guard).
 */
export function PdfPreviewOverlay() {
  const { t } = useTranslation()
  const preview = usePdfPreview()

  if (!Capacitor.isNativePlatform() || !preview) return null

  const handleDownload = () => {
    saveOrShareBlob(preview.blob, preview.filename, 'application/pdf')
  }

  return (
    // z-[90]: deliberately *below* AppLockGate's z-[100] — if biometric lock engages
    // while a preview (potentially showing sensitive customer/invoice data) is open,
    // the opaque lock screen must render on top and block interaction, not the other
    // way around.
    <div className="fixed inset-0 z-[90] flex flex-col bg-background">
      <div className="flex items-center justify-between gap-3 border-b border-border px-3 py-2">
        <Button
          variant="ghost"
          size="icon"
          onClick={closePdfPreview}
          aria-label={t('pdfPreview.back')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <span className="flex-1 truncate text-center text-sm font-medium">{preview.title}</span>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleDownload}
          aria-label={t('pdfPreview.download')}
        >
          <Download className="h-5 w-5" />
        </Button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-6">
        <div className="mx-auto flex max-w-2xl flex-col gap-4">
          {preview.pageDataUrls.map((pageDataUrl, index) => (
            <img
              key={index}
              src={pageDataUrl}
              alt={t('pdfPreview.pageAlt', { page: index + 1 })}
              loading="lazy"
              className="w-full rounded-lg border border-border bg-white shadow-md"
            />
          ))}
        </div>
      </div>
    </div>
  )
}
